import { z } from 'zod';
/**
 * The one coordinate transform, used by both halves of the PDF form feature.
 *
 * The browser draws the page with pdf.js and positions real `<input>` elements over it; the
 * server fills the same page with pdf-lib. If those two compute positions independently they
 * drift by a few points, and a few points is a signature sitting off the line on a Virginia
 * sworn disclosure — a document that then looks wrong to whoever receives it and cannot be
 * corrected without asking the person to sign again.
 *
 * So the rule is: one function, in packages/shared, called from both sides, tested from both.
 *
 * TWO COORDINATE SYSTEMS, and every mistake here is silent:
 *
 *   PDF     origin BOTTOM-left, y increases upward, units are points (1/72 inch),
 *           and the origin is the MediaBox's lower-left corner, which is not always (0,0).
 *   Device  origin TOP-left, y increases downward, units are CSS pixels after scaling.
 *
 * The y-flip and the MediaBox offset are what break first. `/Rotate` is what breaks second,
 * and only on the scanned state forms that happen to carry it.
 */
/** A page's MediaBox, as PDF points: [x0, y0, x1, y1] with (x0,y0) the lower-left corner. */
export const mediaBoxSchema = z
    .tuple([z.number(), z.number(), z.number(), z.number()])
    .refine(([x0, y0, x1, y1]) => x1 > x0 && y1 > y0, {
    message: 'MediaBox must have a positive width and height',
});
/** The only values a PDF `/Rotate` may take. Anything else is a malformed page. */
export const pdfRotationSchema = z.union([
    z.literal(0),
    z.literal(90),
    z.literal(180),
    z.literal(270),
]);
/**
 * A rectangle in PDF points, in ABSOLUTE user space — the same coordinates the PDF itself
 * uses, NOT offsets from the MediaBox corner.
 *
 * So on a page whose MediaBox is [20, 30, 632, 822], the left edge of the page is x = 20 and
 * a rect at x = 0 is off the page entirely. The transform subtracts the MediaBox origin; the
 * map must not subtract it first.
 *
 * This is worth stating precisely because a mapping tool that assumed otherwise would be
 * wrong by exactly (x0, y0) on every page — invisible on the ordinary [0,0,612,792] page and
 * wrong on every scanned state form, which are the documents this feature exists for.
 *
 * `y` is the BOTTOM edge, because that is what PDF means by y. Storing a top edge here would
 * read more naturally to anyone coming from CSS and would be wrong in a way that only shows
 * up as fields drawn upside down.
 */
export const pdfRectSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
});
/**
 * The page's affine transform, matching pdf.js's `PageViewport` exactly.
 *
 * Matching matters: the canvas underneath is rendered BY pdf.js, so an overlay computed from
 * a different convention lands next to the ink rather than on it. Returned as the usual
 * [a, b, c, d, e, f], applied as `X = a*x + c*y + e`, `Y = b*x + d*y + f`, before scaling.
 */
function pageTransform(mediaBox, rotation) {
    const [x0, y0, x1, y1] = mediaBox;
    switch (rotation) {
        case 90:
            return [0, 1, 1, 0, -y0, -x0];
        case 180:
            return [-1, 0, 0, 1, x1, -y0];
        case 270:
            return [0, -1, -1, 0, y1, x1];
        default:
            // Unrotated: translate to the MediaBox origin and flip y.
            return [1, 0, 0, -1, -x0, y1];
    }
}
function applyTransform([a, b, c, d, e, f], x, y) {
    return { x: a * x + c * y + e, y: b * x + d * y + f };
}
/** The page's size on screen, in device pixels. Width and height swap under 90 and 270. */
export function devicePageSize(page) {
    const [x0, y0, x1, y1] = page.mediaBox;
    const wide = x1 - x0;
    const tall = y1 - y0;
    const swapped = page.rotation === 90 || page.rotation === 270;
    return {
        width: (swapped ? tall : wide) * page.scale,
        height: (swapped ? wide : tall) * page.scale,
    };
}
/**
 * PDF points to device space.
 *
 * Both corners are transformed and then normalised, rather than transforming the origin and
 * scaling the size. Under rotation the width and height exchange roles, so carrying them
 * through unrotated produces a box of the right size in the wrong orientation — which looks
 * plausible on a portrait page and is wrong on every landscape one.
 */
export function toDeviceRect(rect, page) {
    const transform = pageTransform(page.mediaBox, page.rotation);
    const a = applyTransform(transform, rect.x, rect.y);
    const b = applyTransform(transform, rect.x + rect.width, rect.y + rect.height);
    return {
        left: Math.min(a.x, b.x) * page.scale,
        top: Math.min(a.y, b.y) * page.scale,
        width: Math.abs(b.x - a.x) * page.scale,
        height: Math.abs(b.y - a.y) * page.scale,
    };
}
/**
 * Device space back to PDF points.
 *
 * The inverse exists so the two directions can be tested against each other. A transform that
 * is subtly wrong usually still round-trips against ITSELF, so the round-trip test is
 * necessary and not sufficient — the fixed expectations in the test file are what pin it to
 * pdf.js's actual convention.
 */
export function toPdfRect(rect, page) {
    const [x0, y0, x1, y1] = page.mediaBox;
    const s = page.scale;
    const left = rect.left / s;
    const top = rect.top / s;
    const width = rect.width / s;
    const height = rect.height / s;
    switch (page.rotation) {
        case 90:
            return { x: top + x0, y: left + y0, width: height, height: width };
        case 180:
            return { x: x1 - left - width, y: top + y0, width, height };
        case 270:
            return { x: x1 - top - height, y: y1 - left - width, width: height, height: width };
        default:
            return { x: left + x0, y: y1 - top - height, width, height };
    }
}
/**
 * Is this rectangle actually on its page?
 *
 * Checked at publish rather than at fill time. A field mapped off the page produces a PDF
 * that looks fine until somebody reads the part that is missing, and by then it has been
 * signed. A tenth of a point of tolerance absorbs the rounding in a mapping tool without
 * admitting a genuinely misplaced field.
 */
export function isWithinPage(rect, mediaBox, tolerance = 0.1) {
    const [x0, y0, x1, y1] = mediaBox;
    return (rect.x >= x0 - tolerance &&
        rect.y >= y0 - tolerance &&
        rect.x + rect.width <= x1 + tolerance &&
        rect.y + rect.height <= y1 + tolerance);
}
//# sourceMappingURL=geometry.js.map